/* global DeepLinkFavoriting, FavoritesEnabled, FetchV1Favorites */
/* eslint-disable import/no-unused-modules */
import React from 'react';
import _ from 'lodash';
import {
  helperGetApiAccountRoute,
  helperGetApiHeaders,
  helperGetApiKey,
  helperGetFavoriteDetailsApiRoute,
} from 'helpers/ApiHelper';
import { isBrowserIE } from 'helpers/GlobalHelper';
import { renderGrid } from 'helpers/GridHelper';
import style from 'screens/Shows/Shows.css';
import { getDisplayBrand, getNetworkLogoUrl } from 'helpers/ContentHelper';
import { helperGetRelativeUrl, helperGetAccountUrl } from 'helpers/RouteHelper';
import {
  setAnonymousFavoritesInStorage,
  getAnonymousFavoritesFromStorage,
  ANONYMOUS_ACCOUNT_TYPE,
} from 'helpers/AnonymousHelper';
import {
  getFavorites,
  getV1Favorites,
  setFavorites,
  setFavoritesIE,
  deleteFavorites,
  deleteFavoritesIE,
  getAccountCookie,
} from 'helpers/Account';
import { upgradeJWT as helperUpgradeJWT } from 'helpers/auth.clientless';
import { setLocalStorageItem, getLocalStorageItem, removeLocalStorageItem } from 'helpers/Storage';
import Favorite, { FAVORITE_TYPE_ICON, FAVORITE_PLUS_CIRCLE } from 'components/Favorite';
import tileGridTheme from 'themes/TileGridAccountFavorites.css';
import tileTheme from 'themes/TileAccountFavorites.css';
import favoriteTheme from 'themes/FavoriteAccount.css';
import listStyle from 'themes/ListAccountFavorites.module.scss';
import {
  FAVORITES_TYPE_PERSONALITY,
  getAccountAnalyticsEvents,
  PageNames,
  SegmentSources,
  tealiumEventPageTypes,
  TrackEvents,
} from '@foxcorp/delta-web-analytics';
import { COLLECTION_THUMBNAIL_IMAGE, renderImage } from 'helpers/ImageHelper';

import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import { userAttributeEvents } from 'helpers/OptimizelyHelper';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    renderList: false,
  },
  [SITE_PLATFORM_WEB]: {
    renderList: false,
  },
  [SITE_PLATFORM_TABLET]: {
    renderList: true,
  },
  [SITE_PLATFORM_MOBILE]: {
    renderList: true,
  },
};
export const helperGetPerks = () => _.get(window.ExternalConfig, 'profile.perks');
const FAVORITES_STORAGE_NAME = 'FOX_FAVORITES';

const DEEP_LINK_FAVORITE = 'favorite=true';
const DEEP_LINK_FAVORITE_NOT_AUTHED = 'DEEP_LINK_FAVORITED';

function gridItemProcessor(theme, favoritesManager, options) {
  const { item } = options;
  if (!item) return null;

  const {
    id: showID,
    network,
    images: { HD: defaultImage, still: fallbackImageObject },
    screenUrl: url,
    name: title,
    recommendationId,
    favoritableItems,
  } = item;
  let image = defaultImage;
  if (!defaultImage && fallbackImageObject) {
    const { HD: fallbackImage } = fallbackImageObject;
    image = fallbackImage;
  }
  const pageType = _.get(tealiumEventPageTypes, PageNames.SETTINGS);
  const favoriteData = {
    showID,
    source: SegmentSources.MY_ACCOUNT,
    location: pageType,
    networkId: network,
    seriesName: title,
    recommendationId,
    favoritableItems,
  };

  const overlay = (
    <Favorite
      manager={favoritesManager}
      data={favoriteData}
      theme={theme}
      type={FAVORITE_TYPE_ICON}
      iconType={FAVORITE_PLUS_CIRCLE}
    />
  );

  const displayBrand = getDisplayBrand(item);
  const networkIcon = getNetworkLogoUrl(displayBrand, 25);
  return {
    id: showID,
    title,
    titleLink: helperGetRelativeUrl({ url }),
    networkIcon,
    image,
    overlay,
  };
}

function listItemProcessor(theme, favoritesManager, options) {
  const { item } = options;
  if (!item) return null;
  const {
    id: showID,
    screenUrl: url,
    name: title,
    network,
    recommendationId,
    favoritableItems,
  } = item;
  const pageType = _.get(tealiumEventPageTypes, PageNames.SETTINGS);
  const favoriteData = {
    showID,
    source: SegmentSources.MY_ACCOUNT,
    location: pageType,
    networkId: network,
    seriesName: title,
    recommendationId,
    favoritableItems,
  };
  const icon = (
    <Favorite
      manager={favoritesManager}
      data={favoriteData}
      theme={theme}
      type={FAVORITE_TYPE_ICON}
      iconType={FAVORITE_PLUS_CIRCLE}
    />
  );

  return {
    id: showID,
    title,
    icon,
    titleLink: helperGetRelativeUrl({ url }),
  };
}

export function renderFavorites({ breakpoint, favoritesManager, onTitleClick, onTileClick }) {
  const { renderList } = BREAKPOINTS[breakpoint];
  if (!FavoritesEnabled) return null;
  return renderGrid({
    renderImage: image =>
      renderImage({
        breakpoint,
        imageType: COLLECTION_THUMBNAIL_IMAGE,
        ...image,
      }),
    key: 'favorite',
    items: favoritesManager.favoritesDetails,
    listProcessor: listItemProcessor.bind(this, favoriteTheme, favoritesManager),
    gridProcessor: gridItemProcessor.bind(this, favoriteTheme, favoritesManager),
    gridType: 'grid-3-1',
    style,
    tileDetailToggle: false,
    tileTheme,
    renderList,
    listTheme: listStyle,
    tileGridTheme,
    onTitleClick,
    onLinkClick: null,
    onTileClick,
  });
}

const getFavoriteRequestOptions = ({ account, favoritableItems, profileFavorites }) => {
  const { accessToken, deviceId } = account;
  const headers = helperGetApiHeaders({ pathKey: 'showsQuery', accessToken, deviceId });
  const route = helperGetApiAccountRoute('favorites');
  const options = { route, headers, favoritableItems, profileFavorites };
  return options;
};

const setFavoritesInStorage = favorites => {
  const stringifiedFavorites = JSON.stringify(favorites);
  setLocalStorageItem({ key: FAVORITES_STORAGE_NAME, value: stringifiedFavorites });
};

export const getFavoritesFromStorage = () => {
  const favorites = getLocalStorageItem({ key: FAVORITES_STORAGE_NAME });
  return JSON.parse(favorites);
};

const addFavoriteToStorage = favorite => {
  const { favoritableItems } = favorite;
  const favorites = getFavoritesFromStorage();

  favoritableItems.forEach(favoriteItem => {
    const { id } = favoriteItem;
    const filter = favorites?.filter(item => item.favoriteID === id);
    if (!filter?.length) favorites?.push({ favoriteID: id });
  });

  setFavoritesInStorage(favorites);
  return favorites;
};

const removeFavoriteFromStorage = favorite => {
  const { favoritableItems } = favorite;
  let filtered = getFavoritesFromStorage() || [];

  favoritableItems.forEach(favoriteItem => {
    filtered = filtered.filter(item => item.favoriteID !== favoriteItem.id);
  });

  setFavoritesInStorage(filtered);
  return filtered;
};

const mergeV1Favorites = (profileId, v1, v2) => {
  const v2FavoriteIds = v2.map(fav => fav.favoriteID);

  v1.map(favorite => {
    const alreadyInV2 = v2FavoriteIds.indexOf(favorite.showID) !== -1;
    if (alreadyInV2) return null;
    const v2favorite = {
      '@type': 'FavoritesV1',
      favoriteID: favorite.showID,
      id: `${profileId}-${favorite.showID}`,
    };
    v2.push(v2favorite);
    return null;
  });
  return v2;
};

export const getFavoritesForAccount = account =>
  new Promise(async resolve => {
    const options = getFavoriteRequestOptions({ account });
    const { profileId, accountType } = account;
    const v2Favorites = await getFavorites(options);
    const v1Favorites = FetchV1Favorites ? await getV1Favorites({ ...options, account }) : [];
    const result = mergeV1Favorites(profileId, v1Favorites, v2Favorites);
    setFavoritesInStorage(result);
    if (accountType === ANONYMOUS_ACCOUNT_TYPE)
      setAnonymousFavoritesInStorage({ anonymousFavorites: result });
    resolve(result);
  });

export const getFavoritesDetailsForAccount = (account, favorites) => {
  const favoritesShowIds = favorites.map(item => item.favoriteID);
  const route = helperGetFavoriteDetailsApiRoute(favoritesShowIds);
  const options = { ...getFavoriteRequestOptions({ account }), route };

  return new Promise((resolve, reject) => {
    if (!favorites || !favorites.length || !route) {
      resolve([]);
      return;
    }
    getFavorites(options)
      .then(result => {
        const items = _.get(result, 'member', []);
        resolve(items);
      })
      .catch(reject);
  });
};

export const createFavorite = (account, favorite, profileFavorites) => {
  const { favoritableItems } = favorite;
  if (!favoritableItems) return null;
  const { profileId, accountType } = account;
  const { foxProfileState } = userAttributeEvents();
  const { [TrackEvents.ON_FAVORITE]: onFavoriteAnalytics } = getAccountAnalyticsEvents({
    foxProfileState,
    profileId,
  });

  return new Promise((resolve, reject) => {
    const { location, networkId, showID, seriesName, recommendationId } = favorite;
    const isPersonality = favorite.favoriteType === FAVORITES_TYPE_PERSONALITY;
    const options = getFavoriteRequestOptions({ account, favoritableItems, profileFavorites });
    if (accountType === ANONYMOUS_ACCOUNT_TYPE) {
      const anonymousFavorites = getAnonymousFavoritesFromStorage() || [];
      favoritableItems.forEach(favoriteItem => {
        const filter = anonymousFavorites.filter(item => item.showID === favoriteItem.favoriteID);
        if (!filter.length) anonymousFavorites.push(favoriteItem);
      });
      setAnonymousFavoritesInStorage({ anonymousFavorites });
    }

    const setFavoritesAction = isBrowserIE() ? setFavoritesIE : setFavorites;
    setFavoritesAction(options, isPersonality)
      .then(result => {
        addFavoriteToStorage(favorite);
        onFavoriteAnalytics({
          location,
          networkId,
          seriesId: showID,
          seriesName,
          status: 'Yes',
          recommendationId,
        });
        resolve(result);
      })
      .catch(reject);
  });
};

export const deleteFavoritesForAccount = (account, favorite, profileFavorites) => {
  const { profileId, accountType } = account;
  const { favoritableItems } = favorite;
  const { foxProfileState } = userAttributeEvents();
  const { [TrackEvents.ON_UNFAVORITE]: onUnfavoriteAnalytics } = getAccountAnalyticsEvents({
    foxProfileState,
    profileId,
  });
  const options = getFavoriteRequestOptions({ account, favoritableItems, profileFavorites });
  return new Promise((resolve, reject) => {
    const { location, networkId, showID, seriesName, recommendationId } = favorite;

    if (accountType === ANONYMOUS_ACCOUNT_TYPE) {
      const anonymousFavorites = getAnonymousFavoritesFromStorage() || [];
      let filtered = anonymousFavorites;
      favoritableItems.forEach(favoriteItem => {
        filtered = filtered.filter(item => item.favoriteID !== favoriteItem.id);
      });
      setAnonymousFavoritesInStorage({ anonymousFavorites: filtered });
    }

    const deleteFavoritesAction = isBrowserIE() ? deleteFavoritesIE : deleteFavorites;
    deleteFavoritesAction(options)
      .then(result => {
        removeFavoriteFromStorage(favorite);
        onUnfavoriteAnalytics({
          location,
          networkId,
          seriesId: showID,
          seriesName,
          status: 'No',
          recommendationId,
        });
        resolve(result);
      })
      .catch(reject);
  });
};

export const upgradeJWT = (accessToken, deviceId) => {
  const authEndpointConfig = _.get(window.ExternalConfig, 'api.auth');
  const upgradeJWTOptions = {
    authEndpointConfig: { ...authEndpointConfig, apiKey: helperGetApiKey() },
    accessToken,
    deviceId,
  };
  return helperUpgradeJWT(upgradeJWTOptions);
};

export function deepLinkFavoriteSeries({ props, setDeepLinkFavorited }) {
  const { account } = props;
  const querystring = window.location.search.substring(1);
  const hasFavorite = querystring.includes(DEEP_LINK_FAVORITE);
  const favoriteDeepLinkEnabled = FavoritesEnabled && DeepLinkFavoriting;
  if (hasFavorite && favoriteDeepLinkEnabled) {
    if (!account) {
      // If no account, redirect to account login screen with query string
      // Store favorite info in local storage
      const {
        params: { showId: showID },
        seriesData,
        recommendationId,
      } = props;
      const network = _.get(seriesData, 'network');
      const seriesName = _.get(seriesData, 'name');
      const favoriteData = {
        showID,
        source: SegmentSources.DETAIL,
        location: _.get(tealiumEventPageTypes, PageNames.FAVORITES_DETAIL),
        networkId: network,
        seriesName,
        recommendationId,
        seriesId: showID,
      };
      setLocalStorageItem({
        key: DEEP_LINK_FAVORITE_NOT_AUTHED,
        value: JSON.stringify(favoriteData),
      });
      // Redirect to account page
      window.location = `${helperGetAccountUrl()}`;
      return null;
    }
    // If account, add show to users favorites
    return setDeepLinkFavorited(true);
  }
  return null;
}

export function checkDeepLinkFavorite() {
  const account = getAccountCookie();
  const profileId = _.get(account, 'profileId');
  const favoriteDeepLinkEnabled = FavoritesEnabled && DeepLinkFavoriting;
  if (favoriteDeepLinkEnabled) {
    const deepLinkFavoriteData = getLocalStorageItem({ key: DEEP_LINK_FAVORITE_NOT_AUTHED });
    if (deepLinkFavoriteData) {
      const favorite = JSON.parse(deepLinkFavoriteData);
      const options = getFavoriteRequestOptions({ account, favorite });
      const { foxProfileState } = userAttributeEvents();
      const { [TrackEvents.ON_FAVORITE]: onFavoriteAnalytics } = getAccountAnalyticsEvents({
        foxProfileState,
        profileId,
      });
      return new Promise((resolve, reject) => {
        const setFavoriteAction = isBrowserIE() ? setFavoritesIE : setFavorites;
        setFavoriteAction(options)
          .then(result => {
            addFavoriteToStorage(favorite);
            onFavoriteAnalytics(favorite);
            removeLocalStorageItem({ key: DEEP_LINK_FAVORITE_NOT_AUTHED });
            resolve(result);
          })
          .catch(reject);
      });
    }
  }
  return null;
}

export const accountPathMappings = id => {
  const mappings = {
    eProfile: 'edit-profile',
    changePassword: 'change-password',
    manageFavorites: 'manage-favorites',
    parentalControls: 'parental-controls',
    signOut: 'sign-out',
  };
  return mappings[id] || id;
};

export function getCommonTealiumProps() {
  const path = window?.location?.pathname;
  let pageName = window?.utag_data?.page_name;
  let pageType = window?.utag_data?.page_type;
  if (path.includes('/watch')) {
    pageName = 'delta:web:player';
    pageType = 'player';
  }
  if (path.includes('/live-news')) {
    pageName = 'delta:web:live tv:landing';
    pageType = 'live tv';
  }
  return {
    pageName,
    pageType,
  };
}
